import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { fetchAdminUsers, createAdminUser, saveAdminUser } from "@/lib/sacred-library/sacred-library-api";
import { Loader2, UserPlus, KeyRound } from "lucide-react";
import { StaffSetupGuide } from "./StaffSetupGuide";

export function UserManagement({ isAdmin }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [resetId, setResetId] = useState(null);
  const [resetPassword, setResetPassword] = useState("");
  const [form, setForm] = useState({
    email: "",
    password: "",
    name: "",
    role: "editor",
    approval_required: true,
    can_publish_directly: false,
  });
  const [createError, setCreateError] = useState("");

  const load = async () => {
    if (!isAdmin) return;
    setLoading(true);
    setCreateError("");
    try {
      const data = await fetchAdminUsers();
      setUsers(data.users ?? []);
    } catch (e) {
      setUsers([]);
      const detail = e?.response?.data?.detail;
      if (detail) setCreateError(detail);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, [isAdmin]);

  const onCreate = async () => {
    setCreateError("");
    if (!form.email?.trim() || !form.password || form.password.length < 8) {
      setCreateError("Email and password (min 8 characters) are required.");
      return;
    }
    try {
      await createAdminUser(form);
      setForm({ email: "", password: "", name: "", role: "editor", approval_required: true, can_publish_directly: false });
      setShowAddForm(false);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setCreateError(
        detail ||
          "Could not create user. Sign in as admin at /admin/sacred first, or complete bootstrap at /admin/setup."
      );
    }
  };

  const onResetPassword = async (u) => {
    if (!resetPassword || resetPassword.length < 8) {
      alert("Password must be at least 8 characters");
      return;
    }
    await saveAdminUser({ id: u.id, password: resetPassword });
    setResetId(null);
    setResetPassword("");
    load();
  };

  const toggle = async (u, field) => {
    await saveAdminUser({ id: u.id, [field]: !u[field] });
    load();
  };

  if (!isAdmin) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="user-management-forbidden">
        User management requires an admin session.
      </p>
    );
  }

  if (loading) return <Loader2 className="w-6 h-6 animate-spin" />;

  return (
    <div className="space-y-6" data-testid="user-management">
      <StaffSetupGuide />
      <div className="flex flex-wrap gap-2">
        <Button onClick={() => setShowAddForm(!showAddForm)} data-testid="add-user-btn">
          <UserPlus className="w-4 h-4 mr-1" /> Add User
        </Button>
      </div>

      {showAddForm && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Create user</CardTitle>
          </CardHeader>
          <CardContent className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Email</Label>
              <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div>
              <Label>Password</Label>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
            </div>
            <div>
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <Label>Role</Label>
              <select
                className="w-full h-10 border rounded-md px-2 text-sm"
                value={form.role}
                onChange={(e) => {
                  const role = e.target.value;
                  setForm({
                    ...form,
                    role,
                    approval_required: role === "staff" ? true : form.approval_required,
                    can_publish_directly: role === "staff" ? false : form.can_publish_directly,
                  });
                }}
              >
              <option value="admin">admin</option>
              <option value="editor">editor</option>
              <option value="staff">staff (pages only)</option>
              <option value="viewer">viewer</option>
              </select>
            </div>
            {createError && <p className="text-sm text-destructive sm:col-span-2">{createError}</p>}
            <Button onClick={onCreate} data-testid="create-user-submit">
              Create user
            </Button>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Users</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {users.map((u) => (
            <div key={u.id} className="flex flex-wrap items-center gap-3 p-3 border rounded-lg text-sm">
              <div className="flex-1 min-w-[200px]">
                <p className="font-medium">{u.name}</p>
                <p className="text-muted-foreground">{u.email}</p>
                <p className="text-xs capitalize">{u.role}{u.disabled ? " (disabled)" : ""}</p>
              </div>
              {u.role === "editor" && (
                <>
                  <Button size="sm" variant="outline" onClick={() => toggle(u, "approval_required")}>
                    approval: {String(u.approval_required)}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => toggle(u, "can_publish_directly")}>
                    direct publish: {String(u.can_publish_directly)}
                  </Button>
                </>
              )}
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  setResetId(u.id);
                  setResetPassword("");
                }}
                data-testid={`reset-password-${u.id}`}
              >
                <KeyRound className="w-4 h-4 mr-1" /> Reset Password
              </Button>
              <Button size="sm" variant="destructive" onClick={() => saveAdminUser({ id: u.id, disabled: !u.disabled }).then(load)}>
                {u.disabled ? "Enable" : "Disable"}
              </Button>
              {resetId === u.id && (
                <div className="w-full flex flex-wrap gap-2 items-end pt-2 border-t">
                  <div className="flex-1 min-w-[180px]">
                    <Label className="text-xs">New password for {u.email}</Label>
                    <Input
                      type="password"
                      value={resetPassword}
                      onChange={(e) => setResetPassword(e.target.value)}
                      placeholder="Min 8 characters"
                    />
                  </div>
                  <Button size="sm" onClick={() => onResetPassword(u)}>
                    Save password
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setResetId(null)}>
                    Cancel
                  </Button>
                </div>
              )}
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
